/**
 * @module bridges
 * @package @geenius/adapters/react-heroui
 * @description HeroUI-native bridge components for adapter loading, error,
 * and notification surfaces. The adapter contract stays in the React package;
 * this module only adds HeroUI rendering affordances.
 */

"use client";

import {
  Alert,
  Button,
  Card,
  Spinner,
  ToastProvider,
  toast,
} from "@heroui/react";
import {
  Component,
  type ComponentProps,
  type ErrorInfo,
  type JSX,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";

type SpinnerSize = ComponentProps<typeof Spinner>["size"];
type SpinnerColor = ComponentProps<typeof Spinner>["color"];
type ToastPlacement = ComponentProps<typeof ToastProvider>["placement"];
type ToastStatus =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "error";

/**
 * Props accepted by the HeroUI loading bridge.
 *
 * @property loading Optional loading flag. Defaults to `true` when omitted.
 * @property isLoading Alias for `loading` used by async adapter states.
 * @property label Primary loading message.
 * @property description Optional supporting loading message.
 * @property children Content rendered when loading is explicitly false.
 * @property className Optional class name passed to the HeroUI card.
 * @property size HeroUI spinner size.
 * @property spinnerColor HeroUI spinner color.
 */
export interface LoadingBridgeProps {
  loading?: boolean;
  isLoading?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  size?: SpinnerSize;
  spinnerColor?: SpinnerColor;
}

/**
 * Renders a HeroUI loading surface for async adapter initialization.
 *
 * @param props Loading bridge props.
 * @returns A HeroUI card containing a spinner, or children when loading is false.
 */
export function LoadingBridge({
  loading,
  isLoading,
  label = "Loading adapters",
  description,
  children,
  className,
  size = "md",
  spinnerColor = "current",
}: LoadingBridgeProps): JSX.Element {
  const active = isLoading ?? loading ?? true;

  if (!active) {
    return <>{children}</>;
  }

  return (
    <Card
      className={className}
      role="status"
      aria-live="polite"
      data-testid="gn-adapters-heroui-loading-bridge"
    >
      <Card.Content className="flex items-center gap-3 p-4">
        <Spinner size={size} color={spinnerColor} aria-hidden="true" />
        <span className="min-w-0">
          <span className="block text-sm font-medium">{label}</span>
          {description ? (
            <span className="block text-xs text-default-500">
              {description}
            </span>
          ) : null}
        </span>
      </Card.Content>
    </Card>
  );
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : "Adapter bridge error");
}

/**
 * Props accepted by the HeroUI error boundary bridge.
 *
 * @property children Child subtree protected by the boundary.
 * @property error Optional controlled error for async adapter workflows.
 * @property title Error alert title.
 * @property actionLabel Reset button label.
 * @property fallback Optional custom fallback node or render function.
 * @property onError Called when a child render error is caught.
 * @property onReset Called when the reset action clears a caught error.
 * @property className Optional class name passed to the HeroUI alert.
 */
export interface ErrorBoundaryBridgeProps {
  children?: ReactNode;
  error?: unknown;
  title?: ReactNode;
  actionLabel?: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  className?: string;
}

interface ErrorBoundaryBridgeState {
  caughtError: Error | null;
}

/**
 * Catches React render failures and displays them with HeroUI alert primitives.
 */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = {
    caughtError: null,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const normalizedError = normalizeError(error);
    this.setState({ caughtError: normalizedError });
    this.props.onError?.(normalizedError, errorInfo);
  }

  private reset = (): void => {
    this.setState({ caughtError: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    const error =
      this.props.error !== undefined
        ? normalizeError(this.props.error)
        : this.state.caughtError;

    if (!error) {
      return this.props.children;
    }

    if (typeof this.props.fallback === "function") {
      return this.props.fallback(error, this.reset);
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Alert
        status="danger"
        className={this.props.className}
        role="alert"
        data-testid="gn-adapters-heroui-error-bridge"
      >
        <Alert.Content>
          <Alert.Title>{this.props.title ?? "Adapter error"}</Alert.Title>
          <Alert.Description>{error.message}</Alert.Description>
          <Button
            type="button"
            size="sm"
            variant="danger-soft"
            onPress={this.reset}
          >
            {this.props.actionLabel ?? "Retry"}
          </Button>
        </Alert.Content>
      </Alert>
    );
  }
}

function toastNodeKey(value: ReactNode): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return "react-node";
}

function showToast(
  status: ToastStatus,
  message: ReactNode,
  options: {
    description?: ReactNode;
    timeout?: number;
    onClose?: () => void;
  },
): string {
  if (status === "success") {
    return toast.success(message, options);
  }
  if (status === "warning") {
    return toast.warning(message, options);
  }
  if (status === "danger" || status === "error") {
    return toast.danger(message, options);
  }
  if (status === "info") {
    return toast.info(message, options);
  }

  return toast(message, { ...options, variant: "default" });
}

/**
 * Props accepted by the HeroUI toast bridge.
 *
 * @property children Optional subtree rendered inside the HeroUI toast provider.
 * @property message Declarative toast message to emit when `open` is true.
 * @property description Optional toast description.
 * @property status Toast severity.
 * @property open Whether the toast should be emitted.
 * @property toastKey Stable dedupe key for repeated declarative renders.
 * @property timeout Optional toast timeout in milliseconds.
 * @property onClose Called by HeroUI when the toast closes.
 * @property onShown Receives the HeroUI toast id after emission.
 * @property placement HeroUI toast placement.
 * @property className Optional class name passed to the HeroUI provider.
 */
export interface ToastBridgeProps {
  children?: ReactNode;
  message?: ReactNode;
  description?: ReactNode;
  status?: ToastStatus;
  open?: boolean;
  toastKey?: string;
  timeout?: number;
  onClose?: () => void;
  onShown?: (toastId: string) => void;
  placement?: ToastPlacement;
  className?: string;
}

/**
 * Provides HeroUI toast rendering and optionally emits a declarative toast.
 *
 * @param props Toast bridge props.
 * @returns A HeroUI toast provider wrapping optional children.
 */
export function ToastBridge({
  children,
  message,
  description,
  status = "default",
  open = false,
  toastKey,
  timeout,
  onClose,
  onShown,
  placement = "bottom end",
  className,
}: ToastBridgeProps): JSX.Element {
  const emittedKeyRef = useRef<string | null>(null);
  const dedupeKey = useMemo(
    () =>
      toastKey ??
      [status, toastNodeKey(message), toastNodeKey(description)].join(":"),
    [description, message, status, toastKey],
  );

  useEffect(() => {
    if (!open) {
      emittedKeyRef.current = null;
      return;
    }

    if (message === undefined || message === null) {
      return;
    }

    if (emittedKeyRef.current === dedupeKey) {
      return;
    }

    emittedKeyRef.current = dedupeKey;
    const toastId = showToast(status, message, {
      description,
      timeout,
      onClose,
    });
    onShown?.(toastId);
  }, [
    dedupeKey,
    description,
    message,
    onClose,
    onShown,
    open,
    status,
    timeout,
  ]);

  return (
    <ToastProvider
      placement={placement}
      className={className}
      data-testid="gn-adapters-heroui-toast-bridge"
    >
      {children}
    </ToastProvider>
  );
}
